import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import ContactForm from "../components/ContactForm";
import { breadcrumbSchema, serviceSchema } from "../lib/schemas";

const REFERENCE_CASES = [
  {
    to: "/works/reale-mutua-essere-libero",
    chip: "Cultural Impact · Format proprietario",
    title: "Reale Mutua × Essere Libero",
    kpi: "Una sponsorship dentro un format proprietario",
    image:
      "/images/assets/e4nbqsyw_01.jpg",
  },
  {
    to: "/works/volleyball-world-italia",
    chip: "Cultural Impact · Community Building",
    title: "Volleyball World Italia",
    kpi: "Una community costruita da zero, 121K follower",
    image:
      "/images/assets/3qbk4db2_2.png",
  },
  {
    to: "/works/credem-extra-ordinary",
    chip: "Social Impact · Brand Journalism",
    title: "Credem Banca × Extra Ordinary",
    kpi: "Un van, tre tappe, un piccolo extra",
    image:
      "/images/assets/3pb7sube_20221017_15-28-55_z62_9552-980x653.jpg",
  },
];

const METHOD_STEPS = [
  {
    num: "01",
    title: "Strategy editoriale",
    body:
      "Definizione del territorio narrativo del brand (per chi commissiona) o del format proprietario (per chi entra come supporter). Decidiamo cosa raccontiamo, perché, con quali codici.",
  },
  {
    num: "02",
    title: "Development del format",
    body:
      "Naming, identità visiva, scaletta delle puntate, costruzione del sistema editoriale. È la fase in cui un'idea diventa un'IP riconoscibile.",
  },
  {
    num: "03",
    title: "Produzione",
    body:
      "Foto, video, articoli, asset social, materiali ADV. Direzione artistica, sound design, post-produzione. Tutto sotto un'unica regia.",
  },
  {
    num: "04",
    title: "Distribuzione",
    body:
      "Pubblicazione coordinata su canali owned (brand), partner (community editoriali), paid (Meta, Google quando serve). Calendario costruito attorno al calendario sportivo.",
  },
  {
    num: "05",
    title: "Vita oltre il lancio",
    body:
      "Riuso del materiale in materiali istituzionali, deck commerciali, casi studio. I contenuti diventano patrimonio.",
  },
];

const DELIVERABLES = [
  { item: "Strategy editoriale", note: "Documento di posizionamento e tone of voice" },
  { item: "Concept e naming del format", note: "Identità completa del format proprietario" },
  { item: "Visual identity & art direction", note: "Sistema visivo riconoscibile" },
  { item: "Produzione video & foto", note: "Direzione, troupe, post-produzione" },
  { item: "Articoli editoriali", note: "Brand journalism long-form" },
  { item: "Distribuzione cross-canale", note: "Owned, partner, paid" },
  { item: "Licensing del format (opzionale)", note: "Per chi vuole replicarlo in altri sport o lingue" },
];

const PROFILES = [
  {
    title: "Brand con sponsorship sportive attive",
    body:
      "Brand che vogliono uscire dalla logica del contenuto sponsorizzato e costruire un'autorità editoriale duratura nel proprio settore di interesse sportivo.",
  },
  {
    title: "Property in cerca di voce propria",
    body:
      "Club, federazioni, leghe che vogliono costruire una narrazione editoriale autonoma rispetto al circuito media tradizionale.",
  },
  {
    title: "Federazioni internazionali che entrano in Italia",
    body:
      "Brand globali dello sport che hanno bisogno di costruire una voce italiana credibile presso il pubblico verticale (vedi: Volleyball World Italia).",
  },
  {
    title: "Aziende con storie da raccontare",
    body:
      "Brand che hanno asset narrativi forti (testimonial, partnership pluriennali, momenti storici da documentare) e vogliono trasformarli in patrimonio editoriale.",
  },
];

const KPIS = [
  {
    dim: "Awareness qualificata",
    kpi: "Sessione media, time on page, completion rate dei video long-form",
  },
  {
    dim: "Affezione",
    kpi: "Salvataggi, condivisioni, commenti significativi",
  },
  {
    dim: "Autorità",
    kpi: "Citazioni esterne, share di voce qualificato, link earned",
  },
  {
    dim: "Patrimoniale",
    kpi: "Riusabilità del materiale in altri canali e formati",
  },
];

const FAQS = [
  {
    q: "Posso usare un vostro format già esistente?",
    a:
      "Sì. Per il volley abbiamo format proprietari attivi (es. Essere Libero) in cui i brand possono entrare come supporter. Per altri sport e per nuovi format, li costruiamo su misura.",
  },
  {
    q: "Cosa è Problemi di Volley?",
    a:
      "È la community editoriale verticale OnSports sul volley. 87K+ follower Instagram, sito proprietario, articoli long-form. È il canale di amplificazione naturale dei nostri format volley.",
  },
  {
    q: "Lavorate anche con video corti per social?",
    a:
      "Sì, ma li costruiamo dentro a un format più ampio. Un reel isolato lavora pochi giorni. Un reel dentro a un format ricorrente lavora per anni.",
  },
  {
    q: "Le produzioni le fate internamente o con partner?",
    a:
      "Direzione artistica e regia sempre internamente. Per le produzioni operative (troupe video, fotografi, post-produzione) lavoriamo con una rete di partner stabili che conosciamo da anni e che applichiamo come team interno.",
  },
  {
    q: "Su quali sport lavorate principalmente?",
    a:
      "Il nostro presidio editoriale più consolidato è sul volley italiano, attraverso la community Problemi di Volley. Lavoriamo con regolarità anche su calcio, basket, motorsport e multi-sport. La metodologia è agnostica rispetto alla disciplina — cambiano i partner editoriali, non l'approccio.",
  },
];

const ServizioContentIP = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Servizi", url: "/servizi" },
    { name: "Content & IP Studio", url: "/servizi/content-ip-studio" },
  ]);
  const serviceJsonLd = serviceSchema({
    name: "Content & IP Studio",
    description: "Studio di creazione format editoriali e IP per brand sportivi e proprietà sportive.",
    slug: "content-ip-studio",
  });

  return (
    <>
      <Seo
        title="Content & IP Studio: format editoriali brand sportivi | OnSports"
        description="Studio OnSports per format editoriali e IP sportivi. Da Problemi di Volley (290K+ follower) a campagne con creator partner. Branded content sportivo nativo."
        path="/servizi/content-ip-studio"
        jsonLd={[breadcrumbJsonLd, serviceJsonLd]}
      />

      {/* SEZ. 1 — HERO */}
      <section
        className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-28"
        data-testid="hero-content-ip"
      >
        <div className="container-os">
          <RevealOnScroll>
            <Link
              to="/servizi"
              className="inline-flex items-center gap-2 text-galactico/70 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
              data-testid="back-to-servizi"
            >
              <ArrowLeft size={16} /> Tutti i servizi
            </Link>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <SectionEyebrow num="03" className="mt-8">
              Content &amp; IP Studio
            </SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <span className="inline-block bg-lime text-dodgers font-condensed uppercase tracking-widest text-[13px] px-3 py-1.5 mt-6">
              Brand journalism · Format proprietari · Production studio
            </span>
          </RevealOnScroll>
          <RevealOnScroll delay={0.15}>
            <h1
              className="display-h1 mt-8 max-w-full"
              style={{
                lineHeight: "0.95",
                letterSpacing: "-0.02em",
                fontSize: "clamp(36px, 7vw, 88px)",
              }}
            >
              <span className="block">Contenuti</span>
              <span className="block">che sono</span>
              <span className="block text-lime">ASSET.</span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.25}>
            <p className="mt-10 body-lg opacity-85 max-w-[68ch]">
              Creiamo format editoriali originali e produzioni premium per
              brand, property e federazioni. Contenuti che lavorano oltre la
              finestra di lancio, format proprietari che diventano IP
              commerciali ricorrenti, brand journalism che costruisce
              autorità nel tempo.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.32}>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[13px] opacity-85">
              <span>Per — Brand, club, property, federazioni</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Output — Format proprietari, video, foto, articoli, IP licenziabili</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 2 — IL PROBLEMA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-problema">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Il problema</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  La maggior parte dei contenuti sponsorizzati muore con la
                  finestra di lancio.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Un brand investe in un video, in una serie di foto, in un
                  articolo. Il contenuto esce, vive due-tre giorni sui
                  social, raccoglie le sue impressions, viene archiviato.
                  Sei mesi dopo, nessuno lo ricorda più, e il brand è
                  costretto a produrne di nuovi per mantenere visibilità. È
                  un modello a perdere: produzione continua, valore
                  residuo zero.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  La causa di questo modello è la commodity dei contenuti
                  sponsorizzati. Quando un brand commissiona contenuto
                  sportivo, lo riceve costruito attorno alla propria voce
                  di brand e alle proprie esigenze di lancio. Il pubblico
                  lo riconosce per quello che è — un contenuto pagato — e
                  ne abbassa l'attenzione di default. È raro che un
                  contenuto sponsorizzato venga davvero "letto", e quasi
                  impossibile che venga ricordato.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Esiste un'alternativa. Costruire format editoriali
                  proprietari — riconoscibili, ricorrenti, autonomi
                  rispetto al brand sponsor — e farli usare dal brand come
                  spazio in cui inserirsi. Questa è la differenza tra un
                  contenuto sponsorizzato e una piattaforma editoriale. La
                  prima si esaurisce. La seconda si accumula.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — COSA FACCIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-cosa-facciamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Cosa facciamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Costruiamo format editoriali, non singoli contenuti.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il Content &amp; IP Studio è il luogo in cui creiamo,
                  produciamo e mantiamo i format editoriali proprietari di
                  OnSports — e quelli su misura per i clienti che ci
                  chiedono di costruirne uno per loro. Operiamo su tre
                  livelli: format ricorrenti (es. Essere Libero, Reach
                  Beyond, The Dugout), brand journalism per la community
                  Problemi di Volley, e produzioni premium one-shot per
                  momenti speciali della stagione (finali europee, lanci,
                  attivazioni di matchday).
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  La differenza fondamentale tra noi e una produzione
                  tradizionale è la proprietà editoriale. Quando un brand
                  entra in un nostro format, non commissiona contenuto.
                  Diventa supporter di una narrazione che esiste e
                  continuerà a esistere oltre la sua presenza. Questo
                  cambia la percezione del pubblico — il contenuto è letto
                  come editoriale, non come sponsorizzato — e cambia la
                  durabilità del valore: il format funziona per anni, il
                  brand ottiene riconoscibilità ricorrente.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Per chi non ha un format proprietario in casa e vuole
                  costruirne uno, lavoriamo da zero. Concept, identità,
                  naming, scaletta editoriale, calendario di pubblicazione,
                  sistema di produzione. Quando il format è pronto, può
                  vivere sui canali del cliente o sui nostri (Problemi di
                  Volley, 87K+ follower, è la community editoriale
                  verticale più consolidata del volley italiano).
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL METODO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-metodo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Il metodo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Format prima, contenuti dopo.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {METHOD_STEPS.map((step, i) => (
              <RevealOnScroll key={step.num} delay={i * 0.05}>
                <div
                  className="border border-lime p-6 md:p-8 bg-dodgers/[0.02] h-full flex flex-col"
                  data-testid={`method-step-${step.num}`}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-lime text-3xl md:text-4xl leading-none">
                      {step.num}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl leading-tight uppercase">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {step.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 5 — COSA INCLUDIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-includiamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Cosa includiamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Sette deliverable, una macchina editoriale.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="deliverables-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal w-[36%]">Deliverable</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {DELIVERABLES.map((d, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top">{d.item}</td>
                      <td className="py-4 align-top opacity-80">{d.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — PER CHI È */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-per-chi-e">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Per chi è</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Quattro profili tipici.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {PROFILES.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.06}>
                <div
                  className="border border-lime p-6 md:p-8 h-full"
                  data-testid={`profile-${i}`}
                >
                  <h3 className="font-display text-lg md:text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {p.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 7 — CASI DI RIFERIMENTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-casi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Casi di riferimento</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Format, applicati.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {REFERENCE_CASES.map((c, i) => (
              <RevealOnScroll key={c.to} delay={i * 0.06}>
                <Link
                  to={c.to}
                  className="group block focus:outline-none"
                  data-hover="true"
                  data-testid={`case-card-${i}`}
                >
                  <div className="overflow-hidden bg-galactico/[0.05]" style={{ aspectRatio: "4 / 3" }}>
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                      draggable="false"
                    />
                  </div>
                  <span className="block mt-4 font-condensed uppercase tracking-widest text-lime text-[11px]">
                    ● {c.chip}
                  </span>
                  <h3 className="mt-2 font-display text-xl md:text-2xl leading-tight group-hover:text-lime transition-colors">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-[1.55] opacity-85">{c.kpi}</p>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 8 — MISURAZIONE & KPI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-kpi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Misurazione</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Misuriamo l'autorità, non l'esposizione.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="kpi-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal w-[30%]">Dimensione</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">KPI tipici</th>
                  </tr>
                </thead>
                <tbody>
                  {KPIS.map((k, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top font-display text-base md:text-lg">{k.dim}</td>
                      <td className="py-4 align-top opacity-90">{k.kpi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — FAQ */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-faq">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Domande frequenti</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Le domande più frequenti.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="max-w-[960px]" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <RevealOnScroll key={i} delay={i * 0.04}>
                <details
                  className="group border-b border-galactico/20 py-5 md:py-6"
                  data-testid={`faq-${i}`}
                >
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <span className="font-display text-lg md:text-xl leading-tight pr-4">
                      {f.q}
                    </span>
                    <span
                      className="flex-shrink-0 mt-1 text-lime transition-transform duration-300 group-open:rotate-45"
                      aria-hidden="true"
                    >
                      <Plus size={22} strokeWidth={2.2} />
                    </span>
                  </summary>
                  <p className="mt-4 text-[15px] md:text-[1rem] leading-[1.7] opacity-90 max-w-[760px]">
                    {f.a}
                  </p>
                </details>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 10 — CONTATTO IN-PAGE */}
      <section
        className="bg-galactico text-dodgers py-24 md:py-32 border-t border-dodgers/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-3xl">
          <RevealOnScroll>
            <SectionEyebrow>● Cominciamo</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <h2 className="display-h2 mt-6 max-w-[28ch]" data-testid="cta-h2">
              Costruiamo insieme i tuoi format <span className="text-lime">EDITORIALI</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Compila il form qui sotto, ti rispondiamo entro 24 ore lavorative.
              Una call di 30 minuti per capire se siamo gli interlocutori giusti.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10">
              <ContactForm
                tone="dark"
                defaultSubject="Richiesta consulenza — Content & IP Studio"
                compact
              />
            </div>
          </RevealOnScroll>
        </div>
      </section>
    </>
  );
};

export default ServizioContentIP;
